import GoTrue from "gotrue-js";

/**
 * Netlify Identity, talked to directly rather than through the widget.
 *
 * The widget owns its own modal, and when it fails to pick up an invite token
 * from the URL there is nothing the app can do about it — you get a dialog that
 * says "email not confirmed" and no way forward. Driving GoTrue ourselves means
 * accepting an invite is one form: set a password, submit, you're in.
 *
 * It also sidesteps a real problem with email clients. Outlook's Safe Links
 * prefetches URLs, which silently burns a one-shot confirmation token before the
 * recipient ever clicks. An invite token can't be burned that way, because
 * redeeming it requires a password the prefetcher doesn't have.
 */

/**
 * Created on first use rather than at import time, so the pure helpers below can
 * be imported and tested in Node without a DOM.
 */
let client = null;
export function identity() {
  if (!client) {
    client = new GoTrue({
      APIUrl: `${window.location.origin}/.netlify/identity`,
      setCookie: false,
    });
  }
  return client;
}

/* ---------------------------------------------------------------- tokens */

const TOKEN_KINDS = {
  invite_token: "invite",
  confirmation_token: "confirmation",
  recovery_token: "recovery",
  email_change_token: "email_change",
};

/**
 * Finds an Identity token in a URL.
 *
 * Netlify puts it in the fragment (`#invite_token=…`), but mail clients and
 * link-wrappers sometimes move it to the query string, so both are checked.
 * Exported separately from any React code so it can be unit tested.
 */
export function readAuthToken({ hash = "", search = "" } = {}) {
  for (const source of [hash, search]) {
    const raw = String(source).replace(/^[#?]/, "");
    if (!raw) continue;
    const params = new URLSearchParams(raw);
    for (const [param, kind] of Object.entries(TOKEN_KINDS)) {
      const token = params.get(param);
      if (token) return { kind, token, param };
    }
    // Netlify's error redirects come back as #error=…&error_description=…
    const error = params.get("error_description") || params.get("error");
    if (error) return { kind: "error", message: humanError(error) };
  }
  return null;
}

/** GoTrue's messages are terse and lowercase; make them presentable. */
export function humanError(err) {
  const raw = typeof err === "string" ? err : err?.message || err?.error_description || "";
  const text = String(raw).replace(/\+/g, " ").trim();
  const map = [
    [/email not confirmed/i, "That email hasn't been confirmed yet. Use the link in your invitation, or ask for a new one."],
    [/invalid.*(grant|login|password)|no user found|wrong password/i, "That email and password don't match an account."],
    [/(token|invite).*(expired|invalid)|not found/i, "That link has expired or has already been used. Ask for a fresh invitation."],
    [/password.*(short|least|6)/i, "Passwords need to be at least 8 characters."],
    [/already (registered|been registered|exists)/i, "There's already an account with that email. Sign in instead, or reset the password."],
    [/signups? not allowed|disable_signup/i, "This site is invite-only. Ask an owner to invite you."],
    [/failed to fetch|networkerror/i, "Couldn't reach the sign-in service. Check your connection and try again."],
  ];
  for (const [pattern, message] of map) if (pattern.test(text)) return message;
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Something went wrong. Try again.";
}

/** Drops the token from the address bar so a refresh can't replay a spent one. */
export function clearAuthToken() {
  if (window.location.hash || window.location.search) {
    window.history.replaceState({}, "", window.location.pathname);
  }
}

/* ---------------------------------------------------------------- session */

export const currentUser = () => identity().currentUser();

/** Restores a stored session and confirms it's still valid with the server. */
export async function restoreSession() {
  const user = identity().currentUser();
  if (!user) return null;
  try {
    return await identity().validateCurrentSession();
  } catch {
    try { await user.logout(); } catch { /* already gone */ }
    return null;
  }
}

/** The bearer token for API calls. Refreshes itself when close to expiry. */
export async function getToken() {
  const user = identity().currentUser();
  if (!user) return null;
  try {
    return await user.jwt();
  } catch {
    return null;
  }
}

export const signIn = (email, password) => identity().login(String(email).trim(), password, true);
export const signUp = (email, password, name) =>
  identity().signup(String(email).trim(), password, name ? { full_name: name } : undefined);
export const acceptInvite = (token, password) => identity().acceptInvite(token, password, true);
export const confirmSignup = (token) => identity().confirm(token, true);
export const requestPasswordReset = (email) => identity().requestPasswordRecovery(String(email).trim());

/** Recovery links log you in, then you set the new password. */
export async function completePasswordReset(token, password) {
  const user = await identity().recover(token, true);
  await user.update({ password });
  return user;
}

export async function signOut() {
  const user = identity().currentUser();
  if (user) {
    try { await user.logout(); } catch { /* token already invalid */ }
  }
  window.location.href = "/";
}

/** Whether this site allows self-signup, so the UI can hide what won't work. */
export async function siteSettings() {
  try {
    return await identity().settings();
  } catch {
    return null;
  }
}

/** Client-side password rule, matched to the message in humanError. */
export const passwordProblem = (password, confirmation) => {
  if (!password || password.length < 8) return "Passwords need to be at least 8 characters.";
  if (confirmation !== undefined && password !== confirmation) return "Those two passwords don't match.";
  return null;
};
